import { useState } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/lib/supabaseClient";
import { trackEvent } from "@/lib/analytics";

export default function Login() {
  const router = useRouter();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showForgot, setShowForgot] = useState(false);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setError(error.message);
    } else {
      setMessage("Check your email for a password reset link.");
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (isSignUp) {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) {
        setError(error.message);
      } else {
        trackEvent("Signup");
        router.push("/dashboard");
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        setError(error.message);
      } else {
        router.push("/dashboard");
      }
    }
    setLoading(false);
  };

  return (
    <>
      <Head>
        <title>{isSignUp ? "Sign Up" : "Sign In"} - ClearCut</title>
      </Head>
      <div className="flex min-h-screen bg-bg text-white">
        {/* Left decorative panel */}
        <div className="relative hidden w-1/2 items-center justify-center overflow-hidden border-r border-border lg:flex">
          {/* Geometric background */}
          <div className="absolute inset-0 opacity-[0.04]">
            <div className="checkerboard h-full w-full" />
          </div>

          {/* Accent glow */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[400px] rounded-full bg-accent/[0.06] blur-[100px]" />

          {/* Diagonal lines decoration */}
          <div className="absolute inset-0">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent"
                style={{
                  top: `${20 + i * 15}%`,
                  left: "-10%",
                  right: "-10%",
                  transform: `rotate(${-15 + i * 3}deg)`,
                }}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.8 }}
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative z-10 px-12 text-center"
          >
            <div className="flex items-center justify-center gap-1 mb-6">
              <span className="font-display text-4xl font-extrabold text-white">
                Clear
              </span>
              <span className="font-display text-4xl font-extrabold text-accent">
                Cut
              </span>
            </div>
            <p className="text-muted text-lg max-w-xs mx-auto">
              Surgical precision for your product images.
            </p>
          </motion.div>
        </div>

        {/* Right form panel */}
        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="w-full max-w-sm"
          >
            {/* Mobile logo */}
            <div className="mb-8 flex items-center gap-0.5 lg:hidden">
              <span className="font-display text-xl font-bold text-white">Clear</span>
              <span className="font-display text-xl font-bold text-accent">Cut</span>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={showForgot ? "forgot" : isSignUp ? "signup" : "signin"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
              >
                <h1 className="font-display text-3xl font-bold">
                  {showForgot ? "Reset password" : isSignUp ? "Create account" : "Welcome back"}
                </h1>
                <p className="mt-2 text-sm text-muted">
                  {showForgot
                    ? "Enter your email and we\u2019ll send a reset link."
                    : isSignUp
                    ? "Sign up to start removing backgrounds."
                    : "Sign in to your ClearCut account."}
                </p>
              </motion.div>
            </AnimatePresence>

            <form onSubmit={showForgot ? handleForgotPassword : handleSubmit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-muted">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-white placeholder-muted-dim outline-none transition-all focus:border-accent/50 focus:ring-1 focus:ring-accent/20"
                  placeholder="you@example.com"
                />
              </div>
              {!showForgot && (
                <div>
                  <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-muted">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-white placeholder-muted-dim outline-none transition-all focus:border-accent/50 focus:ring-1 focus:ring-accent/20"
                    placeholder="At least 6 characters"
                  />
                </div>
              )}

              {!isSignUp && !showForgot && (
                <button
                  type="button"
                  onClick={() => {
                    setShowForgot(true);
                    setError(null);
                    setMessage(null);
                  }}
                  className="text-xs text-muted transition-colors hover:text-accent"
                >
                  Forgot password?
                </button>
              )}

              <AnimatePresence>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-sm text-red-400"
                  >
                    {error}
                  </motion.p>
                )}
                {message && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-sm text-accent"
                  >
                    {message}
                  </motion.p>
                )}
              </AnimatePresence>

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.01, boxShadow: "0 0 25px rgba(200,255,0,0.2)" }}
                whileTap={{ scale: 0.98 }}
                className="w-full rounded-lg bg-accent py-3 text-sm font-bold text-black disabled:opacity-50 transition-shadow"
              >
                {loading
                  ? "Loading..."
                  : showForgot
                  ? "Send reset link"
                  : isSignUp
                  ? "Create account"
                  : "Sign in"}
              </motion.button>

              {!showForgot && (
                <>
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs text-muted">or</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={async () => {
                      await supabase.auth.signInWithOAuth({
                        provider: "google",
                        options: { redirectTo: `${window.location.origin}/auth/callback` },
                      });
                    }}
                    className="flex w-full items-center justify-center gap-3 rounded-lg border border-border py-3 text-sm font-medium text-white transition-colors hover:border-border-light"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </motion.button>
                </>
              )}

              {isSignUp && !showForgot && (
                <p className="text-center text-xs text-muted">
                  By signing up, you agree to our{" "}
                  <Link href="/terms" className="text-accent hover:underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link href="/privacy" className="text-accent hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              )}
            </form>

            <button
              onClick={() => {
                if (showForgot) {
                  setShowForgot(false);
                } else {
                  setIsSignUp(!isSignUp);
                }
                setError(null);
                setMessage(null);
              }}
              className="mt-6 text-sm text-muted transition-colors hover:text-white"
            >
              {showForgot
                ? "Back to sign in"
                : isSignUp
                ? "Already have an account? Sign in"
                : "Don't have an account? Sign up"}
            </button>
          </motion.div>
        </div>
      </div>
    </>
  );
}
